import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Bell, Search, Menu, X, User, LogOut, Settings } from "lucide-react";
import Sidebar from "./Sidebar";
import { useRole } from "../../contexts/RoleContext";
import { useNavigate } from "react-router-dom";

function Header() {
  const { user, logout } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();

  const { role, loading } = useRole();

  const notifications = [
    {
      id: 1,
      title: "New user registered",
      message: "John Doe has registered as a new user",
      time: "5 minutes ago",
      read: false,
    },
    {
      id: 2,
      title: "Subscription purchased",
      message: "A user purchased Premium subscription",
      time: "1 hour ago",
      read: false,
    },
    {
      id: 3,
      title: "Server update complete",
      message: "The server maintenance is complete",
      time: "2 hours ago",
      read: true,
    },
  ];

  const unreadCount = notifications.filter((n) => !n.read).length;

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
    if (showNotifications) setShowNotifications(false);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showProfileDropdown) setShowProfileDropdown(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header className="bg-white shadow-sm z-10">
      <div className="px-4 py-3 flex justify-between items-center">
        {/* Mobile Menu Button */}
        {showMobileMenu ? (
          <div>
            <Sidebar
              onLogout={handleLogout}
              role={role}
              isMobile={showMobileMenu}
              toggleMobileMenu = {toggleMobileMenu}
            />
          </div>
        ) : (
          <button
            className="md:hidden text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={toggleMobileMenu}
          >
            <Menu className="h-6 w-6" />
          </button>
        )}

        {/* Search Bar */}
        <div className="hidden md:flex relative flex-grow max-w-md">
          {/* <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          /> */}
        </div>

        {/* Right-side Actions */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            {/* <button
              className="p-1 rounded-full text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={toggleNotifications}
            >
              <Bell className="h-6 w-6" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 inline-block w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button> */}

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-1 z-10 ring-1 ring-black ring-opacity-5">
                <div className="px-4 py-2 border-b">
                  <h3 className="text-sm font-medium">Notifications</h3>
                </div>
                {notifications.length > 0 ? (
                  <div>
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`px-4 py-3 hover:bg-gray-50 ${
                          !notification.read ? "bg-blue-50" : ""
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <h4 className="text-sm font-medium">
                            {notification.title}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {notification.time}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {notification.message}
                        </p>
                      </div>
                    ))}
                    <div className="px-4 py-2 border-t text-center">
                      <button className="text-sm text-primary hover:text-primary-700">
                        View all notifications
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-6 text-center text-gray-500">
                    No notifications
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="relative">
            <button
              className="flex items-center text-gray-700 hover:text-gray-900 focus:outline-none"
              onClick={toggleProfileDropdown}
            >
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.full_name}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <User className="h-5 w-5 text-gray-500" />
                )}
              </div>
              <span className="hidden md:block ml-2 text-sm font-medium">
                {user?.full_name || "Admin User"}
              </span>
            </button>

            {/* Profile Dropdown */}
            {showProfileDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 ring-1 ring-black ring-opacity-5">
                <div className="px-4 py-3 border-b">
                  <p className="text-sm">{user?.full_name || "Admin User"}</p>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {user?.email || "admin@example.com"}
                  </p>
                </div>
                <a
                  href="/profile"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <User className="h-4 w-4 mr-2" />
                  Your Profile
                </a>
                {/* <a
                  href="#settings"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </a> */}
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
